import { useRef, useEffect } from 'react'
// signature_pad chargé dynamiquement dans useEffect pour réduire le bundle initial
import { useAppStore } from '../store/useAppStore'
import Header from '../components/ui/Header'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import toast from 'react-hot-toast'
import { Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react'

// ── Pad de signature ─────────────────────────────────────────────────────────
interface SigPadProps {
  label: string;
  name: string;
  onSave: (dataUrl: string) => void;
  saved: boolean;
  disabled?: boolean;
}

function SigPad({ label, name, onSave, saved, disabled = false }: SigPadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const padRef    = useRef<any>(null)
  const lastSigRef = useRef<string | null>(null)   // ✅ FIX Symptôme 2 — backup signature avant resize

  useEffect(() => {
    const canvas = canvasRef.current as HTMLCanvasElement | null
    if (!canvas) return
    let cancelled = false

    const resize = () => {
      // ✅ FIX Symptôme 2 — sauvegarder le contenu AVANT le resize
      if (padRef.current && !(padRef.current as any).isEmpty()) {
        lastSigRef.current = (padRef.current as any).toDataURL('image/png')
      }
      const ratio = window.devicePixelRatio || 1
      canvas.width  = canvas.offsetWidth  * ratio
      canvas.height = canvas.offsetHeight * ratio
      canvas.getContext('2d')?.scale(ratio, ratio)
      ;(padRef.current as any)?.clear()
      // ✅ FIX Symptôme 2 — restaurer le contenu APRÈS le resize
      if (lastSigRef.current && padRef.current) {
        (padRef.current as any).fromDataURL(lastSigRef.current)
      }
    }

    // Chargement dynamique de signature_pad (~30 KB économisés sur le bundle initial)
    ;(async () => {
      const { default: SignaturePad } = await import('signature_pad')
      if (cancelled) return
      padRef.current = new SignaturePad(canvas, { minWidth: 2, maxWidth: 4, penColor: '#111827' }) as any
      if (disabled) (padRef.current as any).off()
      resize()
      window.addEventListener('resize', resize)
    })()

    return () => {
      cancelled = true
      window.removeEventListener('resize', resize)
    }
  }, [disabled])

  useEffect(() => {
    if (!padRef.current) return
    disabled ? padRef.current.off() : padRef.current.on()
  }, [disabled])

  const clear = () => padRef.current?.clear()
  const save  = () => {
    if (padRef.current?.isEmpty()) return toast.error(`Signature ${name} requise`)
    onSave(padRef.current.toDataURL('image/png'))
    toast.success(`Signature ${name} enregistrée ✓`)
  }

  return (
    <Card className={`p-4 ${saved ? 'border-2 border-green-400' : ''} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-800">{label}</h3>
        {saved && <span className="text-green-600 text-sm font-semibold">✓ Signé</span>}
      </div>
      <div
        className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50"
        style={{ height: 140 }}
      >
        <canvas ref={canvasRef} className="w-full h-full" />
      </div>
      <div className="flex gap-3 mt-3">
        <Button onClick={clear} variant="ghost" size="sm" className="flex-1">
          <Trash2 size={14} /> Effacer
        </Button>
        <Button onClick={save} variant={saved ? 'success' : 'primary'} size="sm" className="flex-1">
          {saved ? '✓ Mis à jour' : 'Valider la signature'}
        </Button>
      </div>
    </Card>
  )
}

// ── Bloc départ ──────────────────────────────────────────────────────────────
interface DepartureBlockProps {
  checked: boolean;
  onChange: (val: boolean) => void;
}

function DepartureBlock({ checked, onChange }: DepartureBlockProps) {
  return (
    <Card className="p-4 bg-amber-50 border-amber-300">
      <div className="flex gap-2 mb-3">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 font-medium leading-relaxed">
          Avant de signer, veuillez vous assurer que les photographies sont de qualité
          suffisante et lisibles. Votre signature vaut acceptation de la qualité des images
          et renonciation à toute contestation ultérieure concernant leur lisibilité lors
          du retour du véhicule.
        </p>
      </div>
      <label className="flex items-start gap-3 cursor-pointer mt-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="mt-0.5 w-5 h-5 accent-blue-600 flex-shrink-0 cursor-pointer"
        />
        <span className={`text-sm font-semibold leading-snug ${checked ? 'text-blue-700' : 'text-gray-700'}`}>
          Je certifie avoir vérifié la qualité et la lisibilité de toutes les photographies
        </span>
      </label>
    </Card>
  )
}

// ── Bloc retour ──────────────────────────────────────────────────────────────
interface ReturnBlockProps {
  value: string | null;
  onChange: (val: string) => void;
}

function ReturnBlock({ value, onChange }: ReturnBlockProps) {
  return (
    <Card className="p-4 bg-blue-50 border-blue-200">
      <div className="flex gap-2 mb-4">
        <CheckCircle2 size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800 font-semibold">
          Déclaration de fin de location
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {[
          { val: 'no_accident',  label: "Je certifie n'avoir eu aucun accident durant cette location" },
          { val: 'had_accident', label: 'Je déclare avoir eu un accident durant cette location' },
        ].map(opt => (
          <label key={opt.val} className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="accident_declaration"
              value={opt.val}
              checked={value === opt.val}
              onChange={() => onChange(opt.val)}
              className="mt-0.5 w-5 h-5 accent-blue-600 flex-shrink-0 cursor-pointer"
            />
            <span className={`text-sm font-medium leading-snug ${
              value === opt.val
                ? opt.val === 'had_accident' ? 'text-red-700 font-bold' : 'text-blue-700 font-bold'
                : 'text-gray-700'
            }`}>
              {opt.label}
            </span>
          </label>
        ))}
      </div>
      {value === 'had_accident' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-xs text-red-700 font-medium">
            ⚠️ Un constat amiable devra être complété. Merci de le signaler à l'agence.
          </p>
        </div>
      )}
    </Card>
  )
}

// ── Écran principal ──────────────────────────────────────────────────────────
export default function SignatureScreen() {
  const inspection              = useAppStore(s => s.inspection)
  const updateInspection        = useAppStore(s => s.updateInspection)
  const saveCompletedInspection = useAppStore(s => s.saveCompletedInspection)
  const setStep                 = useAppStore(s => s.setStep)

  const isReturn = inspection.type === 'retour'
  const isPrep   = !!(inspection.contract as any)?._isPrep

  // ✅ Lus depuis le store → survivent à la rotation tablette
  const photosChecked       = inspection.photosChecked       ?? false
  const accidentDeclaration = inspection.accidentDeclaration ?? null

  const setPhotosChecked       = (val: boolean) => updateInspection({ photosChecked: val })
  const setAccidentDeclaration = (val: string) => updateInspection({ accidentDeclaration: val })

  const canSign = isReturn ? !!accidentDeclaration : photosChecked

  const handleSaveAgent  = (sig: string) => updateInspection({ signatureAgent: sig })
  const handleSaveClient = (sig: string) => updateInspection({ signatureClient: sig })

  // ✅ GUIDE VISUEL — basé sur snapshot React (ok pour affichage seulement)
  const showAgentMissing  = canSign && !inspection.signatureAgent
  const showClientMissing = canSign && !!inspection.signatureAgent && !inspection.signatureClient
  const showHint = !canSign || showAgentMissing || showClientMissing

  const validate = () => {
    // ✅ VALIDATION — 100% via getState() → jamais de race condition
    const fresh = useAppStore.getState().inspection

    if (!canSign)                 return toast.error(
      isReturn
        ? 'Sélectionnez une déclaration accident'
        : 'Cochez la vérification des photos'
    )
    if (!fresh.signatureAgent)    return toast.error("Signature de l'agent requise")
    if (!isPrep && !fresh.signatureClient) return toast.error('Signature du client requise')

    const completedAt = new Date().toISOString()

    updateInspection({
      completedAt,
      ...(isReturn  && { accidentDeclaration }),
      ...(!isReturn && { photosVerified: true }),
    })

    const id = `${fresh.contract?.id}_${fresh.type}_${Date.now()}`
    saveCompletedInspection(id, {
      ...useAppStore.getState().inspection,
      completedAt,
      ...(isReturn  && { accidentDeclaration }),
      ...(!isReturn && { photosVerified: true }),
    })

    setStep('done')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header
        title="Signatures"
        subtitle="Les deux parties"
        showBack
        backStep="security"
      />
      <div className="flex-1 p-6 max-w-2xl mx-auto w-full flex flex-col gap-5">

        <Card className="p-4 bg-gray-100 border-gray-200">
          <p className="text-sm text-gray-600 font-medium text-center">
            Les deux signatures certifient que l'état des lieux a été réalisé contradictoirement.
          </p>
        </Card>

        {!isReturn && (
          <DepartureBlock
            checked={photosChecked}
            onChange={setPhotosChecked}
          />
        )}

        {isReturn && (
          <ReturnBlock
            value={accidentDeclaration}
            onChange={setAccidentDeclaration}
          />
        )}

        <SigPad
          label="Signature de l'agent"
          name="de l'agent"
          saved={!!inspection.signatureAgent}
          onSave={handleSaveAgent}
          disabled={!canSign}
        />
        {!isPrep && (
          <SigPad
            label="Signature du client"
            name="du client"
            saved={!!inspection.signatureClient}
            onSave={handleSaveClient}
            disabled={!canSign}
          />
        )}

        {isPrep && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 text-center">
              🔧 Mode préparation — la signature client sera recueillie au comptoir lors de la liaison.
            </p>
          </div>
        )}

        {/* ✅ Guide visuel — snapshot React ok ici (affichage seul, pas de logique) */}
        {showHint && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-sm text-amber-700 font-medium text-center">
              {!canSign
                ? isReturn
                  ? '① Sélectionnez une déclaration accident'
                  : '① Cochez la vérification des photos'
                : showAgentMissing
                  ? "② Faites signer l'agent"
                  : '③ Faites signer le client'}
            </p>
          </div>
        )}

        {/* ✅ BOUTON JAMAIS DISABLED
            → la validation réelle se fait dans validate() via getState()
            → le disabled React était la source du bug intermittent            */}
        <Button
          onClick={validate}
          size="xl"
          className="w-full mt-auto"
        >
          {isPrep ? 'Publier la préparation →' : 'Générer et envoyer le PDF →'}
        </Button>

      </div>
    </div>
  )
}